import {
  deriveAnnotationCamera,
  Vec3,
  type ViewerAnimTrack,
  type ViewerAnnotation,
  type ViewerInitOptions,
  type ViewerSceneOptions,
} from '@mindcloudxai/shared';
import { Color } from 'playcanvas';
import { importSettings } from '../../submodules/supersplat-viewer/src/settings';
export type { SupersplatViewerTrack };
import type {
  Annotation as SettingsAnnotation,
  AnimTrack as SupersplatViewerTrack,
  ExperienceSettings,
} from '../../submodules/supersplat-viewer/src/settings';

// ---------------------------------------------------------------------------
// ExperienceSettings 构造 — 把 Viewer 契约的中性输入合并为 supersplat
// ExperienceSettings v2，viewer 内部据此闭环执行（运镜/热点/后效）。
// ---------------------------------------------------------------------------

export function parseHexColor(value: string): Color {
  const hex = value.replace('#', '');
  const norm =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => `${c}${c}`)
          .join('')
      : hex;
  const n = parseInt(norm, 16);
  if (isNaN(n)) return new Color(0, 0, 0, 1);
  return new Color(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, 1);
}

function parseHexColorArray(value: string | undefined): [number, number, number] {
  const color = parseHexColor(value ?? '#000000');
  return [color.r, color.g, color.b];
}

function toTuple3(v: { x: number; y: number; z: number }): [number, number, number] {
  return [v.x, v.y, v.z];
}

/** 中性 ViewerAnimTrack → supersplat settings v2 animTrack，补默认值。
 *
 * supersplat 内部 AnimState 按 `times × frameRate` 取样，要求 times 单位为帧；
 * ViewerAnimTrack 契约统一以秒为单位，在此处按固定 30fps 转换并下发 frameRate，
 * 不把 frameRate 这个伪需求暴露到 viewer 对外契约。
 */
export const SUPERSPLAT_FRAME_RATE = 30;

export function toSettingsAnimTrack(track: ViewerAnimTrack): SupersplatViewerTrack {
  const count = track.keyframes.times.length;
  return {
    name: track.name ?? 'track',
    duration: track.duration,
    frameRate: SUPERSPLAT_FRAME_RATE,
    loopMode: track.loopMode ?? 'repeat',
    interpolation: track.interpolation ?? 'spline',
    smoothness: track.smoothness ?? 0,
    keyframes: {
      times: track.keyframes.times.map((t) => t * SUPERSPLAT_FRAME_RATE),
      values: {
        position: track.keyframes.values.position,
        target: track.keyframes.values.target,
        fov: track.keyframes.values.fov ?? new Array(count).fill(60),
        up: track.keyframes.values.up,
      },
    },
  };
}

/**
 * 中性 ViewerAnnotation → settings v2 annotation。
 * v2 schema 要求 camera 必填；缺省时静态推导一个环视机位兜底，
 * focusAnnotation 运行时仍会基于当前相机重新推导。
 */
function toSettingsAnnotation(ann: ViewerAnnotation): SettingsAnnotation {
  const camera = deriveAnnotationCamera(ann);
  return {
    position: toTuple3(Vec3.from(ann.position)),
    title: ann.title,
    text: ann.text ?? '',
    camera: {
      initial: {
        position: toTuple3(Vec3.from(camera.position)),
        target: toTuple3(Vec3.from(camera.target)),
        fov: camera.fov ?? 60,
      },
    },
  } as SettingsAnnotation;
}

function createDefaultSettings(init: ViewerInitOptions): ExperienceSettings {
  const camera = init.camera;
  return {
    version: 2,
    tonemapping: 'none',
    highPrecisionRendering: false,
    background: {
      color: parseHexColorArray(init.background),
      skyboxUrl: undefined,
    },
    postEffectSettings: {
      sharpness: { enabled: false, amount: 0 },
      bloom: { enabled: false, intensity: 1, blurLevel: 2 },
      grading: { enabled: false, brightness: 0, contrast: 1, saturation: 1, tint: [1, 1, 1] },
      vignette: { enabled: false, intensity: 0.5, inner: 0.3, outer: 0.75, curvature: 1 },
      fringing: { enabled: false, intensity: 0.5 },
    },
    animTracks: [],
    cameras: camera
      ? [
          {
            initial: {
              position: toTuple3(Vec3.from(camera.position)),
              target: toTuple3(Vec3.from(camera.target)),
              fov: camera.fov ?? 75,
            },
          },
        ]
      : [],
    annotations: [],
    startMode: 'default',
  };
}

/**
 * 合并 init 默认值、透传 settings 与场景级覆盖项（annotations/animTrack/autoPlayAnim）。
 */
export function buildExperienceSettings(
  init: ViewerInitOptions,
  scene: ViewerSceneOptions,
): ExperienceSettings {
  const base = scene.settings ? importSettings(scene.settings) : createDefaultSettings(init);

  if (scene.skyboxUrl) {
    base.background.skyboxUrl = scene.skyboxUrl;
  }
  if (scene.annotations) {
    base.annotations = scene.annotations.map(toSettingsAnnotation);
  }
  if (scene.animTrack) {
    const settingsTrack = toSettingsAnimTrack(scene.animTrack);
    base.animTracks = [settingsTrack];
    // 用轨迹第一帧覆盖 cameras[0].initial，避免开场时相机先跳到旧初始位再回到轨迹起点
    const pos = settingsTrack.keyframes.values.position;
    const tgt = settingsTrack.keyframes.values.target;
    const fovArr = settingsTrack.keyframes.values.fov;
    if (pos.length >= 3 && tgt.length >= 3) {
      const cam0 = {
        initial: {
          position: [pos[0], pos[1], pos[2]] as [number, number, number],
          target: [tgt[0], tgt[1], tgt[2]] as [number, number, number],
          fov: fovArr?.[0] ?? 60,
        },
      };
      base.cameras = [cam0, ...base.cameras.slice(1)];
    }
  }
  if (scene.autoPlayAnim !== undefined) {
    base.startMode = scene.autoPlayAnim && base.animTracks.length > 0 ? 'animTrack' : 'default';
  }

  return base;
}
